import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export function AuthCallback() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirectPath, setRedirectPath] = useState('');
  const { fetchProfile } = useAuth(); // Get fetchProfile from context

  useEffect(() => {
    console.log('[AuthCallback] Component mounted.');
    const handleAuthCallback = async () => {
      try {
        console.log('[AuthCallback] Handling callback...');
        // Check for tokens in the hash; if not present, try the query string.
        const urlParams = window.location.hash
          ? new URLSearchParams(window.location.hash.substring(1))
          : new URLSearchParams(window.location.search);
        
        const accessToken = urlParams.get('access_token');
        const refreshToken = urlParams.get('refresh_token');

        console.log("[AuthCallback] URL Params:", urlParams.toString());
        console.log("[AuthCallback] Access Token found:", !!accessToken);
        // console.log("Access Token:", accessToken); // Avoid logging sensitive token

        if (!accessToken) throw new Error('No access token found in URL');

        console.log("[AuthCallback] Setting session with token...");
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || '' // Provide empty string if null
        });
        
        if (sessionError) {
          console.error("[AuthCallback] Error setting session:", sessionError);
          throw sessionError;
        }
        console.log("[AuthCallback] Session set successfully.");
        
        console.log("[AuthCallback] Getting user details...");
        const { data: { user }, error: userError } = await supabase.auth.getUser();
                
        if (userError) {
          console.error("[AuthCallback] Error getting user:", userError);
          throw userError;
        }
        if (!user) throw new Error('User not found after setting session');
        console.log("[AuthCallback] User found:", user.id, user.email);


        // Call context's fetchProfile to update global state AND get profile for redirect logic
        // This ensures the context is updated correctly for subsequent renders/guards
        console.log(`[AuthCallback] Calling context fetchProfile for user ${user.id}`);
        await fetchProfile(user.id);
        
        // Re-fetch profile directly here ONLY if context fetchProfile failed or for immediate redirect logic?
        // Decision: Rely on context's fetchProfile result for consistency.
        // We need to get the profile *after* fetchProfile has updated the context.
        // Let's refetch here just for the redirect decision *after* context update attempt.
        
        console.log("[AuthCallback] Fetching profile directly for redirect decision...");
         const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role, first_name, last_name, phone_number')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error("[AuthCallback] Error fetching profile directly:", profileError);
          // Potentially allow redirect even if profile fetch fails, e.g., to /profile for completion
          // Or throw error to redirect to login? Let's throw for now.
          throw new Error(profileError.message || 'Error fetching profile for redirect');
        }
        if (!profile) {
          // This case should ideally not happen if sign-up creates a profile stub
          console.error("[AuthCallback] Profile not found for user:", user.id);
          // Redirect to profile page to force creation/completion
          setRedirectPath('/profile');
          // throw new Error('Profile not found');
        } else {
            console.log("[AuthCallback] Profile fetched directly:", profile);

            // Determine redirect path based on profile completeness
            if (!profile.first_name || !profile.last_name || !profile.phone_number) {
              console.log("[AuthCallback] Profile incomplete, setting redirect to /profile");
              setRedirectPath('/profile');
            } else {
              const rolePath = `/${profile.role}`;
              console.log(`[AuthCallback] Profile complete, setting redirect based on role (${profile.role}) to ${rolePath}`);
              setRedirectPath(rolePath);
            }
        }

      } catch (err) {
        console.error('[AuthCallback] Auth callback error:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed during callback');
      } finally {
        console.log('[AuthCallback] Setting loading to false.');
        setLoading(false);
      }
    };

    handleAuthCallback();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Removed fetchProfile dependency, called manually

  if (loading) {
      console.log('[AuthCallback] Rendering loading state.');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
      console.log(`[AuthCallback] Rendering error state, navigating to /login with error: ${error}`);
    // Pass error message to login page via state
    return <Navigate to="/login" state={{ error: `Callback failed: ${error}` }} replace />;
  }

  // Ensure redirectPath is set before navigating
  if (redirectPath) {
      console.log(`[AuthCallback] Rendering Navigate component to: ${redirectPath}`);
      return <Navigate to={redirectPath} replace />;
  } else {
      // Fallback if redirectPath wasn't set somehow (should ideally not happen)
      console.warn('[AuthCallback] No redirect path determined, falling back to /login.');
      return <Navigate to="/login" state={{ error: 'Callback completed but could not determine redirect path.' }} replace />;
  }
}